import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

import { getFavorites } from '../reducers';
import * as actions from '../actions';
import { useRouter, useSignInAuthorization } from './';
import * as ROUTES from '../constants/routes';

export default function useFavorites() {
  const favorites = useSelector(getFavorites, isEqual);
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const isSignedIn = useSignInAuthorization(false);
  const { history } = useRouter();
  const { t } = useTranslation();

  const addFavorite = useCallback(
    async celebrity => {
      try {
        if (!isSignedIn) {
          addToast(t('account.favoritesMustSignIn', 'You should first sign in.'), {
            appearance: 'warning',
            autoDismiss: true,
          });
          history.push(
            `${ROUTES.SIGNIN}?redirect=${ROUTES.CELEBRITY_PROFILE}/${celebrity.id}/${celebrity.firstname}-${celebrity.lastname}`
          );
          return;
        }
        await dispatch(actions.addFavorite(celebrity.id));
        addToast(t('account.favoritesAddSuccess', 'Added to your favorites.'), {
          appearance: 'success',
          autoDismiss: true,
        });
      } catch (error) {
        addToast(error.message, {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    },
    [addToast, dispatch, history, isSignedIn, t]
  );

  const removeFavorite = useCallback(
    async celebrity => {
      try {
        await dispatch(actions.removeFavorite(celebrity.id));
        addToast(t('account.favoritesRemoveSuccess', 'Removed from your favorites.'), {
          appearance: 'success',
          autoDismiss: true,
        });
      } catch (error) {
        addToast(error.message, {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    },
    [addToast, dispatch, t]
  );

  return { favorites, addFavorite, removeFavorite };
}
